// src/components/FileCard/FileCard.tsx
import React from 'react';
import { getDocumentClassName } from '@/lib/corgeeHelper';
import type { PersonFile } from '@/services/homeService';
import { downloadAttachment } from '@/services/filesService';
import { authStorage } from '@/lib/storage';
import { config } from '@config/env';
import './FileCard.css';

const VIDEO_EXTENSIONS = ['.3gp', '.mp4', '.mov', '.ogg', '.webm', '.f4v', '.m4v', '.mkv'];
const PDF_EXTENSIONS = ['.pdf'];

/** Map document class name → legacy icon image path */
function getFileIconPath(fileExtension: string): string
{
  const docClass = getDocumentClassName(fileExtension);
  // Usar config.api.baseUrl para obtener la URL ya resuelta con protocolo y tenant
  const apiUrl = config.api.baseUrl;
  const base = apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;

  const iconMap: Record<string, string> = {
    Pdf: 'Images/icon-pdf.png',
    Doc: 'Images/icon-word.png',
    Xls: 'Images/icon-excel.png',
    Ppt: 'Images/icon-ppt.png',
    Zip: 'Images/icon-zip.png',
    Rar: 'Images/icon-rar.png',
    Psd: 'Images/icon-psd.png',
    Ai: 'Images/icon-ai.png',
    Pur: 'Images/icon-pur.png',
    Vid: 'Images/icon-video.png',
    Def: 'Images/icon-default.png',
  };

  return base + (iconMap[docClass] || iconMap.Def);
}

interface FileCardProps {
  file: PersonFile;
  onClickFile: (file: PersonFile) => void;
  onClickTitle?: (conversationId: number) => void;
}

/**
 * Card component for the Files View — shows a file-type icon,
 * file description, author info and conversation details.
 * Matches the legacy project-files-item card structure.
 */
export const FileCard: React.FC<FileCardProps> = ({ file, onClickFile, onClickTitle }) =>
{
  const iconUrl = getFileIconPath(file.FileExtension);

  const handleClick = () =>
  {
    const ext = (file.FileExtension || '').toLowerCase();
    if (VIDEO_EXTENSIONS.includes(ext) || PDF_EXTENSIONS.includes(ext))
    {
      onClickFile(file);
    }
    else
    {
      downloadAttachment(
        file.AttachmentId,
        file.EmbeddedImageId,
        (file.Description || file.FileName) + (file.FileExtension || ''),
        file.FileUrl,
        {
          apiUrl: config.api.baseUrl,
          token: authStorage.getAccessToken() || '',
        },
      );
    }
  };

  return (
    <div className="project-files-item" data-conversationid={file.ConversationId}>
      {/* File type icon area */}
      <div className="project-files-item-details">
        <div className="project-files-item-image">
          <div
            className="project-files-item-thumbnail file-card__icon-area"
            onClick={handleClick}
            onKeyDown={e => e.key === 'Enter' && handleClick()}
            role="button"
            tabIndex={0}
          >
            <img className="file-card__icon-img" src={iconUrl} alt={file.FileExtension} />
          </div>
        </div>

        {/* File info: description + author */}
        <div className="project-files-details">
          <div className="project-files-item-name" onClick={handleClick} title={file.Description}>
            {file.Description}
          </div>
          <div className="project-files-item-author">Created by:</div>
          <div className="project-files-item-author">{file.CreatedByName}</div>
        </div>
      </div>

      {/* Bottom row: layers + conversation icon + title */}
      <div className="project-files-item-card">
        <div className="project-files-item-card-layers">
          {file.Layers?.slice(0, 3).map(layer => (
            <span
              key={`${layer.ConversationId}-${layer.LayerId}`}
              className="layer-tag"
              data-layerid={layer.LayerId}
              style={{ backgroundColor: layer.LayerColor }}
            />
          ))}
        </div>
        <div className="project-files-item-card-icon">
          <i className={file.Icon} />
        </div>
        <div
          className="project-files-item-card-name"
          onClick={() => onClickTitle?.(file.ConversationId)}
          onKeyDown={e => e.key === 'Enter' && onClickTitle?.(file.ConversationId)}
          role="button"
          tabIndex={0}
        >
          {file.Title}
        </div>
      </div>
    </div>
  );
};
